import { moduleState } from 'cerebral'
import { PDFDocument, degrees } from 'pdf-lib'
import { pathOr } from 'ramda'
import { convertInchesUnit } from '../../lib/util/pdfmake-props'
import { upload, url } from '../../lib/util/s3'
import { crudActions } from '../factories'
import { captureException } from '../../lib/util/errors'
import { DesignHuddleClient, printExportOptions } from '../../lib/util/designhuddle'
import apiClient from '../../lib/util/api-client'
import { getPdfArrayBuffer } from '../../lib/util/pdf-array-buffer'

const designHuddleService = apiClient.service('publications/designhuddle')

export const getEditorToken = async ({ props: data }) => designHuddleService.create(data)

const scaleCalendarPages = (pages, paperOrientation) => {
  return pages.map((page) => {
    if (paperOrientation === 'portrait') {
      page.setSize(convertInchesUnit(8.5), convertInchesUnit(11))
      // reducing 11 X 18 to 8.5 X 17 in percentage
      page.scaleContent(0.77, 0.65)
    } else if (paperOrientation === 'landscape') {
      page.setSize(convertInchesUnit(11), convertInchesUnit(8.5))
      // reducing 18 X 11 to 11 X 8.5 in percentage
      page.scaleContent(0.64, 0.77)
      page.setRotation(degrees(90))
    }
    return page
  })
}

const errorMessage = 'There was a problem while uploading this publication. Please try again or contact support if the issue persists.'

export const savePDF = async ({ props, app, publicationsService, store }) => {
  const getEditorToken = app.getSequence(`publications.getEditorToken`)
  const {
    entities = {},
    calendarDocDefinition = {},
    values: { editableLayout: { projectId: editorProjectId } = {}, updateDownloadablePdf = false, calendarOptions = {} } = {},
  } = props
  const showFeedback = app.getSequence('feedback.showFeedback')

  const { publications: [{ _id, includeCalendar = false, title, isFoldableBooklet } = {}] = [] } = entities

  if (editorProjectId && updateDownloadablePdf) {
    try {
      store.set(moduleState`pdf.generating`, true)

      const { access_token } = await getEditorToken()
      const designHuddleClient = new DesignHuddleClient(access_token)
      const editorProject = await designHuddleClient.exportProject(editorProjectId, title, 'pdf', printExportOptions)

      let publication = {}

      if (editorProject) {
        // create a new pdf-lib document
        let newPdfDoc = await PDFDocument.create()
        // load the editor pdf into the pdf-lib document object
        const editorPdfBytes = await fetch(editorProject.download_url).then((res) => res.arrayBuffer())
        const editorPdfDoc = await PDFDocument.load(editorPdfBytes)

        // get editor pdf pages and page count
        let pages = editorPdfDoc.getPages()
        const pageCount = editorPdfDoc.getPageCount()

        let numberOfPages = pageCount

        if (isFoldableBooklet) {
          // Ensure pages are a multiple of 4 for booklet printing
          const remainder = includeCalendar ? (pageCount + 2) % 4 : pageCount % 4

          if (remainder !== 0) {
            const emptyPagesToAdd = Array(4 - remainder).fill(null)
            pages.splice(pageCount - 1, 0, ...emptyPagesToAdd)
          }

          numberOfPages = pages.length

          // Arrange pages for booklet printing
          let left = 0
          let right = pages.length - 1
          let isFront = true

          while (left < right) {
            const pageLeft = pages[left]
            const pageRight = pages[right]

            const page = newPdfDoc.addPage([
              //
              convertInchesUnit(17),
              convertInchesUnit(11),
            ])

            const [frontPage, backPage] = isFront ? [pageRight, pageLeft] : [pageLeft, pageRight]

            if (frontPage) {
              const embeddedFrontPage = await newPdfDoc.embedPage(frontPage)
              page.drawPage(embeddedFrontPage, { x: 0, y: 0 })
            }

            if (backPage) {
              const embeddedBackPage = await newPdfDoc.embedPage(backPage)
              page.drawPage(embeddedBackPage, {
                //
                x: convertInchesUnit(8.5),
                y: 0,
              })
            }
            isFront = !isFront
            left++
            right--
          }

          if (includeCalendar && calendarDocDefinition) {
            // load the calendar pdf into the pdf-lib document object
            const calendarPdfBuffer = await getPdfArrayBuffer(calendarDocDefinition)
            const calendarPdfDoc = await PDFDocument.load(calendarPdfBuffer)

            const calendarPageCount = calendarPdfDoc.getPageCount()
            let calendarPageOne = null
            let calendarPageTwo = null

            if (calendarPageCount < 2) {
              const pages = await newPdfDoc.copyPages(calendarPdfDoc, [0])
              calendarPageOne = pages[0]
            } else {
              const pages = await newPdfDoc.copyPages(calendarPdfDoc, [0, 1])
              calendarPageOne = pages[0]
              calendarPageTwo = pages[1]
            }

            if (calendarOptions.paperOrientation === 'portrait') {
              calendarPageOne.setRotation(degrees(90))
              if (calendarPageTwo) {
                calendarPageTwo.setRotation(degrees(90))
              }
            }

            const insertIndex = newPdfDoc.getPageCount()
            await newPdfDoc.insertPage(insertIndex, calendarPageOne)
            if (calendarPageTwo) {
              await newPdfDoc.insertPage(insertIndex + 1, calendarPageTwo)
            }

            numberOfPages += calendarPageTwo ? 2 : 1
          }
        } else {
          newPdfDoc = editorPdfDoc
          // prepare pdf for non booklet printing

          if (includeCalendar) {
            // load the calendar pdf into the pdf-lib document object
            const calendarPdfBuffer = await getPdfArrayBuffer(calendarDocDefinition)
            const calendarPdfDoc = await PDFDocument.load(calendarPdfBuffer)

            const calendarPageCount = calendarPdfDoc.getPageCount()

            let calendarPageOne = null
            let calendarPageTwo = null

            if (calendarPageCount < 2) {
              const pages = await editorPdfDoc.copyPages(calendarPdfDoc, [0])
              const pagesScaled = scaleCalendarPages(pages, calendarOptions.paperOrientation)
              calendarPageOne = pagesScaled[0]
            } else {
              const pages = await editorPdfDoc.copyPages(calendarPdfDoc, [0, 1])
              const pagesScaled = scaleCalendarPages(pages, calendarOptions.paperOrientation)
              calendarPageOne = pagesScaled[0]
              calendarPageTwo = pagesScaled[1]
            }

            // insert the calendar to the end of newsletter pdf
            const calendarPageIndex = editorPdfDoc.getPageCount()

            await editorPdfDoc.insertPage(calendarPageIndex, calendarPageOne)

            if (calendarPageTwo) {
              await editorPdfDoc.insertPage(calendarPageIndex + 1, calendarPageTwo)
            }
          }

          numberOfPages = newPdfDoc.getPageCount()
        }

        const pdfBytes = await newPdfDoc.save()
        const blob = new Blob([new Uint8Array(pdfBytes)], { type: 'application/pdf' })

        // Uncomment to test without uploading.
        // const pdfUrlData = URL.createObjectURL(blob)
        // return store.set(moduleState`pdf.generating`, false) || window.open(pdfUrlData)

        const signUpload = app.getSequence('uploads.signUpload')

        let publicationDownload = null

        try {
          publicationDownload = await upload(blob, { signUpload, path: 'app/editor/pdf' })
        } catch (error) {
          store.set(moduleState`pdf.generating`, false)
          const body = pathOr(errorMessage, ['response', 'data', 'error', 'message'], error)
          showFeedback({ title: 'Upload Failed', body, type: 'error' })
          return { entities }
        }

        publicationDownload.numberOfPages = numberOfPages
        // Added to the file so that order button visibility switches only after PDF has been regenerated
        publicationDownload.isFoldableBooklet = Boolean(isFoldableBooklet)

        const downloadUrl = url({ fl_attachment: `${title}.pdf` }, publicationDownload)
        publication = await publicationsService.patch(_id, { publicationDownload, downloadUrl })
      }

      store.set(moduleState`pdf.generating`, false)
      return { entities: { ...entities, publications: [publication] } }
    } catch (error) {
      store.set(moduleState`pdf.generating`, false)
      showFeedback({ title: 'Upload Failed', body: error?.message || errorMessage, type: 'error' })
      captureException(error)
    }
  }
}

export const { find, setList, clearList, get, save, remove } = crudActions({ name: 'publications' })
